import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard'
import HomeIcon from '@mui/icons-material/Home'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'

import { paths } from '@/config/paths'
import { useLogout, useUser } from '@/lib/auth'
// import { useAuthorization } from '@/lib/authorization'
import { useAppTheme } from '@/lib/mui-theme'
import { LocationPin } from '@mui/icons-material'

type SideNavigationItem = {
  name: string
  to: string
  icon: React.ElementType
}

const Logo = () => {
  const theme = useTheme()
  const { mode } = useAppTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: mode === 'dark' ? 'primary.light' : 'white',
        textDecoration: 'none',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor:
            mode === 'dark'
              ? alpha(theme.palette.primary.light, 0.2)
              : alpha(theme.palette.common.white, 0.2),
        }}>
        <HomeIcon sx={{ color: mode === 'dark' ? 'primary.light' : 'white', fontSize: 16 }} />
      </Box>
      <Typography
        variant='body2'
        fontWeight={600}
        sx={{
          color: mode === 'dark' ? 'primary.light' : 'white',
          ml: 1,
          letterSpacing: 0.3,
        }}>
        CleanNavi
      </Typography>
    </Box>
  )
}

const UserProfile = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const logout = useLogout({
    onSuccess: () => navigate(paths.auth.login.getHref(location.pathname)),
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { mode } = useAppTheme()
  const user = useUser()

  return (
    <Box sx={{ position: 'relative', mt: 1, mb: 1, px: 1.5, width: '100%' }}>
      {/* プロフィールトリガー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 1,
          borderRadius: theme.shape.borderRadius,
          backgroundColor:
            mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.common.white, 0.1),
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor:
              mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.2)
                : alpha(theme.palette.common.white, 0.2),
          },
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor:
                mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.8)
                  : theme.palette.common.white,
              color: mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main,
            }}>
            <PersonIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography
            variant='body2'
            sx={{
              fontWeight: 500,
              color: mode === 'dark' ? theme.palette.common.white : theme.palette.common.white,
            }}>
            {user.data?.last_name + ' ' + user.data?.first_name}
          </Typography>
        </Box>
        <KeyboardArrowRightIcon
          sx={{
            fontSize: 16,
            color:
              mode === 'dark'
                ? alpha(theme.palette.common.white, 0.7)
                : alpha(theme.palette.common.white, 0.8),
            transform: Boolean(anchorEl) ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      {/* ドロップダウンメニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 2,
          sx: {
            minWidth: 180,
            borderRadius: 1,
            mt: 0.5,
            overflow: 'hidden',
          },
        }}>
        <MenuItem
          onClick={() => {
            navigate(paths.app.profile.getHref())
            setAnchorEl(null)
          }}
          sx={{
            py: 1,
            gap: 1.5,
            fontSize: '0.875rem',
          }}>
          <PersonIcon
            fontSize='small'
            color='primary'
          />
          プロフィール
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            logout.mutate({})
            setAnchorEl(null)
          }}
          sx={{
            py: 1,
            gap: 1.5,
            fontSize: '0.875rem',
            color: 'error.main',
          }}>
          <LogoutIcon fontSize='small' />
          ログアウト
        </MenuItem>
      </Menu>
    </Box>
  )
}

const Progress = () => {
  const location = useLocation()
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setProgress(0)
    setIsLoading(true)
    const timer = setTimeout(() => setIsLoading(false), 1000)
    return () => clearTimeout(timer)
  }, [location.pathname])

  useEffect(() => {
    if (isLoading) {
      const timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress === 100) {
            clearInterval(timer)
            return 100
          }
          const newProgress = oldProgress + 10
          return newProgress > 100 ? 100 : newProgress
        })
      }, 100)

      return () => {
        clearInterval(timer)
      }
    }
  }, [isLoading])

  if (!isLoading) {
    return null
  }

  return (
    <LinearProgress
      variant='determinate'
      value={progress}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: 2,
        zIndex: 9999,
      }}
    />
  )
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const theme = useTheme()
  const { mode } = useAppTheme()
  const location = useLocation()
  // const { checkAccess } = useAuthorization()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // パスに基づいて現在のページ名とパスを取得
  const getPageInfo = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean)

    if (pathSegments.length === 0) {
      return { name: 'ダッシュボード', path: '/' }
    }

    const lastSegment = pathSegments[pathSegments.length - 1]

    const pageMap: Record<string, string> = {
      dashboard: 'ダッシュボード',
      // discussions: 'ディスカッション',
      users: 'ドライバー管理',
      areas: 'エリア管理',
      stations: 'ステーション管理',
      profile: 'プロフィール',
    }

    return {
      name: pageMap[lastSegment] || lastSegment,
      path: `/${pathSegments.join('/')}`,
    }
  }

  const { name: pageName, path: pagePath } = getPageInfo()

  const navigation = [
    { name: 'ダッシュボード', to: paths.app.dashboard.getHref(), icon: DashboardIcon },
    { name: 'エリア管理', to: paths.app.areas.getHref(), icon: LocationOnIcon },
    { name: 'ステーション管理', to: paths.app.stations.getHref(), icon: LocationPin },
    {
      name: 'ドライバー管理',
      to: paths.app.users.getHref(),
      icon: PersonIcon,
    },
    // checkAccess({ allowedRoles: ['admin'] }) && {
    //   name: 'ドライバー管理',
    //   to: paths.app.users.getHref(),
    //   icon: PersonIcon,
    // },
  ].filter(Boolean) as SideNavigationItem[]

  const NavItems = () => (
    <>
      {navigation.map((item) => (
        <Button
          key={item.name}
          component={NavLink}
          to={item.to}
          end={item.to === paths.app.dashboard.getHref()}
          sx={{
            justifyContent: 'flex-start',
            color: 'grey.300',
            borderRadius: theme.shape.borderRadius,
            py: 0.75,
            px: 1.5,
            width: '100%',
            textAlign: 'left',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.light,
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.light,
              },
            },
            '&.active': {
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: `0 0 6px ${alpha(theme.palette.primary.main, 0.5)}`,
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.contrastText,
              },
            },
            textTransform: 'none',
            fontSize: '0.85rem',
            fontWeight: 500,
            transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
              duration: theme.transitions.duration.shorter,
            }),
          }}
          startIcon={
            <item.icon
              sx={{
                color: 'grey.400',
                fontSize: 18,
                mr: 0.5,
                transition: theme.transitions.create('color', {
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            />
          }>
          {item.name}
        </Button>
      ))}
    </>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        bgcolor: 'background.default',
        position: 'relative',
      }}>
      <Progress />

      {/* Sidebar - Desktop */}
      <Box
        component='aside'
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: 220,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: mode === 'dark' ? 'background.paper' : 'primary.main',
          borderRight: 1,
          borderColor: 'divider',
          zIndex: 1200,
          boxShadow: mode === 'dark' ? `0 0 20px rgba(0,0,0,0.1)` : `0 0 20px rgba(0,0,0,0.2)`,
          transition: theme.transitions.create(['box-shadow', 'background-color'], {
            duration: theme.transitions.duration.standard,
          }),
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, height: 56 }}>
          <Logo />
        </Box>
        <Box
          component='nav'
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.5,
            overflowY: 'auto',
          }}>
          <NavItems />
        </Box>
        <Divider
          sx={{
            borderColor:
              mode === 'dark'
                ? alpha(theme.palette.divider, 0.5)
                : alpha(theme.palette.common.white, 0.2),
          }}
        />
        <UserProfile />
      </Box>

      {/* Sidebar - Mobile */}
      <Drawer
        anchor='left'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 220,
            bgcolor: mode === 'dark' ? 'background.paper' : 'primary.main',
            color: theme.palette.primary.contrastText,
            boxShadow: mode === 'dark' ? `0 0 20px rgba(0,0,0,0.1)` : `0 0 20px rgba(0,0,0,0.2)`,
          },
        }}>
        <Box sx={{ p: 1.5, height: 56, display: 'flex', alignItems: 'center' }}>
          <Logo />
        </Box>
        <Box
          component='nav'
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.5,
            overflowY: 'auto',
          }}>
          <NavItems />
        </Box>
        <Divider
          sx={{
            borderColor:
              mode === 'dark'
                ? alpha(theme.palette.divider, 0.5)
                : alpha(theme.palette.common.white, 0.2),
          }}
        />
        <UserProfile />
      </Drawer>

      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          ml: { xs: 0, md: '220px' },
        }}>
        {/* AppBar */}
        <AppBar
          // position='sticky'
          position='relative'
          elevation={0}
          sx={{
            // zIndex: 1100,
            // bgcolor: 'background.paper',
            bgcolor: 'transparent',
            border: 'none',
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.primary',
            boxShadow: 'none',
          }}>
          <Toolbar
            sx={{
              minHeight: { xs: '48px', sm: '48px' },
              px: 1.5,
              py: 0.5,
            }}>
            {/* Mobile menu button */}
            <IconButton
              edge='start'
              color='inherit'
              aria-label='menu'
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1, display: { md: 'none' } }}
              size='small'>
              <MenuIcon
                fontSize='small'
                sx={{ fontSize: 18 }}
              />
            </IconButton>

            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<KeyboardArrowRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
              aria-label='breadcrumb'
              sx={{ fontSize: '0.75rem' }}>
              <Typography
                component={NavLink}
                to={paths.app.dashboard.getHref()}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                }}>
                <HomeIcon sx={{ mr: 0.5, fontSize: 14 }} />
                ホーム
              </Typography>

              {pagePath !== '/' && (
                <Typography
                  color='text.primary'
                  sx={{
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                  }}>
                  {pageName}
                </Typography>
              )}
            </Breadcrumbs>
          </Toolbar>
        </AppBar>

        {/* Main content area */}
        <Box
          component='main'
          sx={{ flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}
